import { useState, useEffect } from 'react';
import { companyService } from '../services/companyService';
import { createCompanyAccountForAdmin, login } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

export const useCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'admin') {
      loadAllCompanies();
    } else if (user?.companyId) {
      loadCurrentCompany();
    }
  }, [user]);

  const loadAllCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companyService.getAll();
      setCompanies(data);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar empresas:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentCompany = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.companyId) {
        setCurrentCompany(null);
        return;
      }
      
      const company = await companyService.getById(user.companyId);
      setCurrentCompany(company);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar empresa atual:', err);
    } finally {
      setLoading(false);
    }
  };

  const addCompany = async (companyData) => {
    if (user?.role !== 'admin') {
      throw new Error('Apenas administradores podem criar empresas');
    }

    try {
      // Salvar dados do admin para reautenticação
      const adminEmail = user.email;
      
      // Solicitar senha do admin para reautenticação após criar empresa
      const adminPassword = window.prompt(
        'Para criar a empresa, confirme sua senha de administrador:', 
        ''
      );
      
      if (!adminPassword) {
        throw new Error('Senha de administrador necessária para continuar');
      }

      // Criar conta de autenticação com senha provisória
      const newCompany = await createCompanyAccountForAdmin(
        companyData.email,
        companyData.temporaryPassword,
        {
          name: companyData.name,
          active: companyData.active !== undefined ? companyData.active : true
        }
      );
      
      // Reautenticar como admin após criar a empresa
      // O Firebase automaticamente desloga o admin quando cria nova conta
      await login(adminEmail, adminPassword);
      
      // Recarregar a lista de empresas
      await loadAllCompanies();
      
      return newCompany;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao adicionar empresa:', err);
      throw err;
    }
  };

  const updateCompany = async (companyId, companyData) => {
    if (user?.role !== 'admin') {
      throw new Error('Apenas administradores podem editar empresas');
    }

    try {
      const updatedCompany = await companyService.update(companyId, companyData);
      setCompanies(prev => 
        prev.map(company => 
          company.id === companyId ? { ...company, ...updatedCompany } : company
        )
      );
      
      // Atualizar empresa atual se for a mesma
      if (currentCompany?.id === companyId) {
        setCurrentCompany(prev => ({ ...prev, ...updatedCompany }));
      }
      
      return updatedCompany;
    } catch (err) {
      setError(err.message);
      console.error('Erro ao atualizar empresa:', err);
      throw err;
    }
  };

  const deleteCompany = async (companyId) => {
    if (user?.role !== 'admin') {
      throw new Error('Apenas administradores podem deletar empresas');
    }

    try {
      await companyService.delete(companyId);
      setCompanies(prev => prev.filter(company => company.id !== companyId));
      
      // Limpar empresa atual se for a mesma
      if (currentCompany?.id === companyId) {
        setCurrentCompany(null);
      }
    } catch (err) {
      setError(err.message);
      console.error('Erro ao deletar empresa:', err);
      throw err;
    }
  };

  const getCompanyById = (companyId) => {
    return companies.find(company => company.id === companyId);
  };

  const getActiveCompanies = () => {
    return companies.filter(company => company.active);
  };

  const getCompanyStats = () => {
    const totalCompanies = companies.length;
    const activeCompanies = companies.filter(c => c.active).length;
    const inactiveCompanies = totalCompanies - activeCompanies;
    
    return {
      total: totalCompanies,
      active: activeCompanies,
      inactive: inactiveCompanies
    };
  };

  return {
    companies,
    currentCompany,
    loading,
    error,
    addCompany,
    updateCompany,
    deleteCompany,
    getCompanyById,
    getActiveCompanies,
    getCompanyStats,
    reload: loadAllCompanies,
    reloadCurrent: loadCurrentCompany
  };
};